import { Box, Backdrop, CircularProgress, Pagination } from "@mui/material";
import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import Chart from "react-apexcharts";
import { useDispatch, useSelector } from "react-redux";
import { prepareModificationsMLCD } from "../analyzeUtils";
import CollapseSection from "../../../components/ui/CollapseSection";
import { setState } from "../analyzeSlice";

export default function MultiLineChart() {
  const dispatch = useDispatch();
  const state = useSelector((store) => store.analyze);
  const {
    selectedScenarioName,
    duration,
    modificationsData,
    modificationsPeriods,
    selectedDataPointsForModification,
    currentPageFlag,
  } = state;

  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const recordsPerPage = 25;
  const legendRef = useRef(null);

  const seriesData = useMemo(() => {
    let data = prepareModificationsMLCD(modificationsData?.lstAnalyzeChartData);
    if (selectedDataPointsForModification.length > 0) {
      data = data.filter((series) =>
        selectedDataPointsForModification.includes(series.name)
      );
    }
    return data;
  }, [modificationsData, selectedDataPointsForModification]);

  const pageCount = useMemo(() => Math.ceil(seriesData.length / recordsPerPage), [seriesData.length]);

  const handlePageChange = useCallback((event, value) => {
    setCurrentPage(value - 1);
    dispatch(setState("currentPageFlag", false));
  }, []);

  const currentData = useMemo(() => {
    const indexOfLastRecord = (currentPage + 1) * recordsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
    return seriesData.slice(indexOfFirstRecord, indexOfLastRecord);
  }, [currentPage, recordsPerPage, seriesData]);

  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 100);
    return () => clearTimeout(timer);
  }, [currentPage, seriesData]);

  useEffect(() => {
    if (currentPageFlag) {
      setCurrentPage(0);
    }
  }, [currentPageFlag]);

  const handleLegendClick = (event) => {
    if (legendRef.current) {
      const scrollPosition = legendRef.current.scrollTop;
      setTimeout(() => {
        if (legendRef.current) {
          legendRef.current.scrollTop = scrollPosition;
        }
      }, 0);
    }
  };

  const options = {
    chart: {
      zoom: {
        enabled: false,
      },
      toolbar: {
        tools: {
          pan: false,
        },
      },
      events: {
        legendClick: handleLegendClick,
      },
    },
    title: {
      text: `Analyze: ${selectedScenarioName} - Last ${duration} Months Modifications`,
      align: "left",
    },
    tooltip: {
      // custom: ({ series, seriesIndex, dataPointIndex, w }) => {
      //   const tooltipItems = series.map((s, index) => `<div>${w.globals.seriesNames[index]}: ${s[dataPointIndex]}</div>`).join('');
      //   return `<div style="max-height: 200px; overflow-y: auto;">${tooltipItems}</div>`;
      // },
      y: {
        formatter: (value) => new Intl.NumberFormat("en-US").format(value),
      },
    },
    xaxis: {
      categories: [...modificationsPeriods],
    },
    yaxis: {
      labels: {
        formatter: (value) => new Intl.NumberFormat("en-US").format(value),
      },
    },
    stroke: {
      width: 3,
    },
    markers: {
      size: 5,
    },
    colors: [
      "#ff0000",
      "#800000",
      "#d2b48c",
      "#ffa500",
      "#ffd700",
      "#00ff00",
      "#008000",
      "#0000ff",
      "#800080",
    ],
    legend: {
      position: "right",
      horizontalAlign: "left",
      offsetX: -15,
      fontWeight: "bold",
      showForSingleSeries: true,
      maxItems: 2,
      itemMargin: {
        vertical: 3,
      },
      containerMargin: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
      },
      onItemClick: {
        toggleDataSeries: true,
      },
      onItemHover: {
        highlightDataSeries: true,
      },
    },
  };

  return (
    <CollapseSection title="Analyse Modification">
      <br />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoading}
      >
        <CircularProgress style={{ position: 'absolute', zIndex: 1300 }} />
      </Backdrop>
      <Box className={"multiLineChart"}>
        <Chart
          options={options}
          series={currentData}
          type="line"
          height={700}
          width={"100%"}
        />
      </Box>
      <div dir="ltr" style={{ display: "flex", width: "100%" }}>
      <Pagination sx={{ml:'auto'}} size="large" count={pageCount} page={currentPage + 1} onChange={handlePageChange} />
      </div>
    </CollapseSection>
  );
}